import { apiFetchWithHeaders } from "../api/client";

/**
 * ═══ LES PHOTOS DE L'ÉVÉNEMENT, ET LES SIENNES ═══
 *
 *   GET /domains/:d/events/:e/users/photos?pageSize=&pageCurrent=   api-v1
 *
 * ⚠️ LA ROUTE MÊLE DEUX CHOSES, ET C'EST VOULU : les photos où le participant a été
 * RECONNU (une ligne `UserPhotos` le liant à la photo) et les photos d'AMBIANCE
 * (`type = 'ambiance'`), qui appartiennent à tout le monde. Sa requête les réunit dans
 * un `LEFT JOIN` avec un `OR`. Les séparer ici donnerait deux galeries là où le produit
 * en montre une.
 *
 * ⚠️ LA PAGINATION N'EST PAS FACULTATIVE. `LIMIT ?` et `OFFSET ?` reçoivent
 * `Number(pageSize)` et `Number(pageCurrent)` sans défaut : omis, ils valent `NaN` et la
 * requête tombe. On les envoie donc toujours, même pour la première page.
 *
 * ⚠️ ET ELLE S'OUVRE AUSSI SANS COMPTE, avec `?badge=<code>` (`isPrivateAndPublic`) —
 * c'est ce que la maquette appelle « accès par code badge ». Cette page-ci ne s'en sert
 * pas : elle vit derrière la session comme le reste du site. Le jour où l'accès public
 * existera, c'est ce paramètre qu'il servira, pas une seconde route.
 */

/**
 * CE QUE L'ALBUM MONTRE.
 *
 * ⚠️ LE FILTRE PART AU SERVEUR, il ne se fait pas ici. Les photos arrivent par pages
 * de vingt-quatre : trier ce qui est déjà chargé donnerait « mes photos » parmi les
 * premières seulement, sous un compteur qui en annonce cent trente et une. Un filtre
 * qui ment sur ce qu'il cache est pire que pas de filtre.
 */
export type Mode = "all" | "mine" | "ambiance";

/**
 * LES ONGLETS QUE L'ALBUM PROPOSE.
 *
 * « Ambiance » se retire au panneau (`FO_MEETING_V2_ALB_AMBIANCE`) : sur un événement
 * sans photographe d'ambiance, c'était un onglet toujours vide.
 *
 * ⚠️ LE RETIRER NE CACHE PAS LES PHOTOS D'AMBIANCE. Elles restent dans « Toutes », qui
 * dit ce qu'elle montre : c'est la route qui les mêle (voir plus haut), et l'onglet
 * n'était qu'un filtre posé dessus.
 */
export function albumModes(ambiance: boolean): Mode[] {
  return ambiance ? ["all", "mine", "ambiance"] : ["all", "mine"];
}

/**
 * ⚠️ UN ONGLET RETIRÉ PENDANT QU'IL EST OUVERT RETOMBE SUR « TOUTES ». Le panneau règle
 * la page en direct : éteindre l'interrupteur en regardant les ambiances aurait laissé
 * la grille sur une pile dont plus aucun bouton ne porte le nom.
 */
export function modeShown(chosen: Mode, modes: readonly Mode[]): Mode {
  return modes.includes(chosen) ? chosen : "all";
}

/** Une photo, telle que la route la rend. */
export type Photo = {
  id: number;
  /** La vignette, retouchée si le photographe l'a fait. */
  vignette: string;
  /** Le fichier à ouvrir et à télécharger, retouché s'il existe. */
  full: string;
  /** `true` quand la photo vient de la reconnaissance, `false` pour une ambiance. */
  mine: boolean;
  /** Quand elle a été prise, si l'appareil l'a écrit. */
  taken: string;
  /** Le nom du fichier livré par le photographe (`EventPictures.fileName`), pour le journal. */
  fileName: string;
};

type Raw = {
  id?: number;
  type?: string | null;
  original?: string | null;
  originalFilter?: string | null;
  xs?: string | null;
  xsFilter?: string | null;
  userId?: number | null;
  dateOriginal?: string | null;
  fileName?: string | null;
};

const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");

function photoDe(raw: Raw): Photo | null {
  if (typeof raw?.id !== "number") return null;

  /**
   * ⚠️ LA VERSION RETOUCHÉE PASSE DEVANT, comme dans la V1 (`originalFilter || original`).
   * Un photographe d'événement livre souvent une seconde passe — recadrage, couleurs — et
   * c'est celle-là que l'organisateur veut voir sortir.
   */
  const full = text(raw.originalFilter) || text(raw.original);
  if (!full) return null;

  return {
    id: raw.id,
    /* ⚠️ ET LA VIGNETTE RETOMBE SUR LA PLEINE QUAND ELLE N'EXISTE PAS. Toutes les photos
       n'ont pas été redimensionnées (`hasThumbnail`) : sans ce repli, la grille affichait
       des trous là où la photo existe pourtant. */
    vignette: text(raw.xsFilter) || text(raw.xs) || full,
    full,
    /* Une ambiance n'a pas d'utilisateur : c'est ce qui la distingue d'une photo
       reconnue, et le seul champ qui le dise. */
    mine: typeof raw.userId === "number",
    taken: text(raw.dateOriginal),
    fileName: text(raw.fileName),
  };
}

/** Une page de photos, et combien il y en a en tout si le serveur le dit. */
export type PagePhotos = {
  photos: Photo[];
  /**
   * ⚠️ COMBIEN DE LIGNES LE SERVEUR A RENDUES, avant qu'on en jette. C'est ce nombre,
   * et pas la longueur de `photos`, qui dit s'il reste une page : une seule ligne
   * inutilisable dans les vingt-quatre — une photo sans fichier, une photo dont
   * l'identifiant manque — et l'album se croyait au bout. Mesuré sur un événement
   * réel : 9 photos affichées, 131 annoncées, et plus rien qui se charge.
   */
  received: number;
  /** `null` quand le serveur ne l'annonce pas : voir ci-dessous. */
  total: number | null;
  /**
   * COMBIEN DANS CHAQUE PILE, pour que les onglets les portent tous les trois.
   *
   * ⚠️ `null` SI LE SERVEUR NE LES DIT PAS — une api-v1 pas encore déployée, ou des
   * en-têtes non exposés au navigateur. Les onglets s'affichent alors SANS nombre :
   * une barre muette se comprend, une barre qui annonce zéro partout se lit comme un
   * album vide.
   */
  counts: Counts | null;
};

/** Combien de photos dans chaque pile. */
export type Counts = Record<Mode, number>;

/**
 * RESTE-T-IL UNE PAGE APRÈS CELLE-CI ?
 *
 * ⚠️ UNE PAGE INCOMPLÈTE EST LA DERNIÈRE, et c'est le seul signal disponible : la
 * route ne dit pas combien de pages existent. Il est exact tant qu'on le lit sur ce
 * que le SERVEUR a envoyé — un `LIMIT` rendu plein peut encore cacher une suite, un
 * `LIMIT` rendu court ne le peut pas.
 */
export function hasMore(page: PagePhotos, perPage: number): boolean {
  return page.received >= perPage;
}

/**
 * ⚠️ LE TOTAL ARRIVE PAR UN EN-TÊTE, ET IL PEUT MANQUER. `X-Total-Count` a été ajouté à
 * la route parce que rien dans le corps ne dit combien de photos existent — la
 * visionneuse annonçait « 1 / 24 » sur un album de 1733, en comptant ce qui était chargé.
 *
 * Il vaut `null` tant que l'API n'est pas déployée, et si le serveur oublie un jour de
 * l'exposer au navigateur (`Access-Control-Expose-Headers`). L'appelant retombe alors sur
 * ce qu'il a sous la main. Une page qui ment sur un total est pire qu'une page qui
 * n'en donne pas.
 */
export async function fetchPhotos(
  domainId: number,
  eventId: number,
  page: number,
  taille: number,
  mode: Mode = "all",
  signal?: AbortSignal,
): Promise<PagePhotos> {
  const { data, headers } = await apiFetchWithHeaders<ReadonlyArray<Raw> | null>(
    `/domains/${domainId}/events/${eventId}/users/photos?pageSize=${taille}&pageCurrent=${page}&mode=${mode}`,
    { signal },
  );

  /* ⚠️ `Number(null)` VAUT ZÉRO, et `Number("")` aussi. Lu sans précaution, un en-tête
     absent aurait donné un album de zéro photo au lieu d'un total inconnu — et zéro est
     une réponse que la visionneuse aurait affichée sans broncher. */
  const nombre = (nom: string): number | null => {
    const raw = headers.get(nom);
    const lu = raw === null || raw.trim() === "" ? NaN : Number(raw);
    return Number.isFinite(lu) && lu >= 0 ? lu : null;
  };

  const all = nombre("X-Count-All");
  const mine = nombre("X-Count-Mine");
  const ambiance = nombre("X-Count-Ambiance");

  const rows = data ?? [];

  return {
    photos: rows.flatMap((p) => {
      const photo = photoDe(p);
      return photo ? [photo] : [];
    }),
    received: rows.length,
    total: nombre("X-Total-Count"),
    /* ⚠️ LES TROIS OU AUCUN. Deux comptes sur trois donneraient une barre d'onglets à
       trous, où le seul sans nombre passerait pour vide. */
    counts:
      all !== null && mine !== null && ambiance !== null
        ? { all, mine, ambiance }
        : null,
  };
}

/**
 * TÉLÉCHARGER UNE PHOTO SOUS UN NOM LISIBLE.
 *
 * ⚠️ ON PASSE PAR UN BLOB, ET IL N'Y A PAS D'AUTRE MOYEN DE LA NOMMER. L'adresse est
 * signée et pointe sur S3 : un `<a download>` posé dessus est ignoré, l'attribut ne
 * traverse pas les domaines. La V1 fait le même détour, et son fichier s'appelle
 * `photo_<id>.jpg` — on garde ce nom, c'est celui que les participants ont déjà.
 */
export async function downloadPhoto(photo: Photo): Promise<void> {
  const answer = await fetch(photo.full, { cache: "no-cache" });
  if (!answer.ok) throw new Error("La photo n'a pas pu être téléchargée.");
  const blob = await answer.blob();

  const url = URL.createObjectURL(blob);
  try {
    const lien = document.createElement("a");
    lien.href = url;
    lien.download = `photo_${photo.id}.jpg`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  } finally {
    /* Pas tout de suite : révoquer l'adresse juste après le clic annule le
       téléchargement dans certains navigateurs. Même règle que le badge. */
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
